import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SUPERUSER_KEY, SUPERUSER_ACTIVE_KEY } from '../constants/config';

export interface AuthState {
  isAuthenticated: boolean;
  token: string;
  profile: Record<string, unknown>;
  isSuperuser: boolean;
  isSuperuserActive: boolean;
}

interface LoginSuccessPayload {
  token: string;
  profile: Record<string, unknown>;
  user: { role: string };
}

// Static and side-effect free. Reading localStorage here at module load made
// initial state depend on import order, which broke the bootstrap writing the
// session before the store was built — it now passes readAuthFromStorage() in
// as preloadedState.
const initialState: AuthState = {
  isAuthenticated: false,
  token: '',
  profile: {},
  isSuperuser: false,
  isSuperuserActive: false,
};

/** Auth state from storage. Call after the redirect callback, pass as preloadedState. */
export const readAuthFromStorage = (): AuthState => {
  let auth0data: { accessToken?: string; profile?: Record<string, unknown> } = {};
  const raw = localStorage.getItem('auth0');
  if (raw) {
    try {
      auth0data = JSON.parse(raw);
    } catch {
      // Corrupt blob — no session rather than a failed boot.
      console.error('*** Could not parse stored auth0 data; starting logged out.');
      return initialState;
    }
  }

  const token = auth0data.accessToken || null;
  const profile = auth0data.profile || null;
  const superuser = localStorage.getItem(SUPERUSER_KEY) === 'true';
  const superuserActive = localStorage.getItem(SUPERUSER_ACTIVE_KEY) === 'true';

  return {
    isAuthenticated: !!(token && profile),
    token: token ? token : '',
    profile: profile ? profile : {},
    isSuperuser: !!(token && profile && superuser),
    isSuperuserActive: !!(token && profile && superuser && superuserActive),
  };
};

// M6 pilot: RTK createSlice. M7 pilot: typed (AuthState + typed payloads). The
// login/logout/setSuperuserActive thunks (still in actionCreators) dispatch these.
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess(state, action: PayloadAction<LoginSuccessPayload>) {
      const { token, profile, user } = action.payload;
      state.isAuthenticated = true;
      state.token = token;
      state.profile = profile;
      state.isSuperuser = user.role === 'superuser';
      state.isSuperuserActive = false;
    },
    // Login failed — auth state stays logged-out. No-op reducer, kept so the login
    // thunk has an action to dispatch on the error path.
    loginError() {},
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.token = '';
      state.profile = {};
      state.isSuperuser = false;
      state.isSuperuserActive = false;
    },
    setSuperuserActive(state, action: PayloadAction<{ isSuperuserActive: boolean }>) {
      state.isSuperuserActive = action.payload.isSuperuserActive;
    },
  },
});

export const { loginSuccess, loginError, logoutSuccess, setSuperuserActive } = authSlice.actions;
export default authSlice.reducer;
